import type Product from '~/models/product';
import { useConfigStore } from '~/store/config';

/**
 * Algolia Insights Tracking Composable
 * 
 * This composable provides methods to track user interactions with products
 * for Algolia Recommend and Analytics features.
 * 
 * Events tracked:
 * - Click events: When users click on products
 * - View events: When users view product detail pages
 * - Add to cart events: When users add products to cart
 * - Purchase events: When users complete a purchase
 * 
 * These events are required for:
 * - Algolia Recommend (Related Products, Frequently Bought Together, Trending Items)
 * - Search Analytics
 * - Personalization (if enabled)
 */

export default function useAlgoliaInsights() {
  const runtimeConfig = useRuntimeConfig();
  const config = useConfigStore();
  
  // Initialize insights library
  const initInsights = () => {
    if (process.client && typeof window !== 'undefined') {
      import('search-insights').then((module) => {
        const aa = module.default;
        
        // Initialize with your Algolia credentials
        aa('init', {
          appId: runtimeConfig.public.algoliaId,
          apiKey: runtimeConfig.public.algoliaSecret,
          useCookie: true, // Track anonymous users with cookies
        });
        
        // Set anonymous user token if not authenticated
        const userToken = getUserToken();
        aa('setUserToken', userToken);
        
        // Store aa globally for use in other methods
        (window as any)._aa = aa;
      });
    }
  };
  
  // Get or generate user token
  const getUserToken = (): string => {
    // TODO: Replace with actual user ID for authenticated users
    // For now, generate anonymous ID or use from cookie
    
    if (process.client) {
      let userToken = localStorage.getItem('_algolia_userToken');
      
      if (!userToken) {
        // Generate anonymous user token
        userToken = `anonymous-${Date.now()}-${Math.random().toString(36).substring(7)}`;
        localStorage.setItem('_algolia_userToken', userToken);
      }
      
      return userToken;
    }
    
    return 'anonymous';
  };
  
  // Track product click from search results
  const trackProductClick = (params: {
    product: Product;
    position?: number;
    queryID?: string;
    indexName?: string;
  }) => {
    if (process.client && (window as any)._aa) {
      // Only track if we have a valid Algolia objectID
      if (!params.product.objectID) {
        console.warn('[Algolia Insights] Skipping click tracking - no objectID');
        return;
      }
      
      const aa = (window as any)._aa;
      const indexName = params.indexName || `${config.algoliaBaseIndex}_products`;
      
      if (params.queryID) {
        // Click from search results - include queryID and position
        aa('clickedObjectIDsAfterSearch', {
          eventName: 'Product Clicked',
          index: indexName,
          objectIDs: [params.product.objectID],
          positions: params.position ? [params.position + 1] : [1], // Algolia uses 1-based positions
          queryID: params.queryID,
        });
      } else {
        // Click from browsing (not from search)
        aa('clickedObjectIDs', {
          eventName: 'Product Clicked',
          index: indexName,
          objectIDs: [params.product.objectID],
        });
      }
      
      console.log('[Algolia Insights] Product Click:', {
        objectID: params.product.objectID,
        queryID: params.queryID,
      });
    }
  };
  
  // Track product view (product detail page)
  const trackProductView = (params: {
    product: Product;
    indexName?: string;
  }) => {
    if (process.client && (window as any)._aa) {
      // Only track if we have a valid Algolia objectID
      if (!params.product.objectID) {
        console.warn('[Algolia Insights] Skipping view tracking - no objectID');
        return;
      }
      
      const aa = (window as any)._aa;
      const indexName = params.indexName || `${config.algoliaBaseIndex}_products`;
      
      aa('viewedObjectIDs', {
        eventName: 'Product Viewed',
        index: indexName,
        objectIDs: [params.product.objectID],
      });
      
      console.log('[Algolia Insights] Product View:', {
        objectID: params.product.objectID,
      });
    }
  };
  
  // Track add to cart
  const trackAddToCart = (params: {
    objectIDs: string[];
    queryID?: string;
    indexName?: string;
  }) => {
    if (process.client && (window as any)._aa) {
      const aa = (window as any)._aa;
      const indexName = params.indexName || `${config.algoliaBaseIndex}_products`;
      
      if (params.objectIDs.length === 0) {
        console.warn('[Algolia Insights] Skipping cart tracking - no objectIDs provided');
        return;
      }
      
      if (params.queryID) {
        // Add to cart after search
        aa('convertedObjectIDsAfterSearch', {
          eventName: 'Product Added to Cart',
          index: indexName,
          objectIDs: params.objectIDs,
          queryID: params.queryID,
        });
      } else {
        // Add to cart from browsing
        aa('convertedObjectIDs', {
          eventName: 'Product Added to Cart',
          index: indexName,
          objectIDs: params.objectIDs,
        });
      }
      
      console.log('[Algolia Insights] Add to Cart:', {
        objectIDs: params.objectIDs,
        queryID: params.queryID,
      });
    }
  };
  
  // Track purchase/order completion
  const trackPurchase = (params: {
    objectIDs: string[];
    objectData?: Array<{ price?: number; quantity?: number; discount?: number }>;
    orderValue?: number;
    queryID?: string;
    indexName?: string;
  }) => {
    if (process.client && (window as any)._aa) {
      const aa = (window as any)._aa;
      const indexName = params.indexName || `${config.algoliaBaseIndex}_products`;
      
      if (params.objectIDs.length === 0) {
        console.warn('[Algolia Insights] Skipping purchase tracking - no objectIDs provided');
        return;
      }
      
      if (params.queryID) {
        // Purchase after search
        aa('purchasedObjectIDsAfterSearch', {
          eventName: 'Product Purchased',
          index: indexName,
          objectIDs: params.objectIDs,
          queryID: params.queryID,
          objectData: params.objectData,
          currency: 'SEK',
          value: params.orderValue,
        });
      } else {
        // Purchase from browsing
        aa('purchasedObjectIDs', {
          eventName: 'Product Purchased',
          index: indexName,
          objectIDs: params.objectIDs,
          objectData: params.objectData,
          currency: 'SEK',
          value: params.orderValue,
        });
      }
      
      console.log('[Algolia Insights] Purchase:', {
        objectIDs: params.objectIDs,
        orderValue: params.orderValue,
        queryID: params.queryID,
      });
    }
  };
  
  // Set authenticated user token (call this after login)
  const setAuthenticatedUser = (userId: string) => {
    if (process.client && (window as any)._aa) {
      const aa = (window as any)._aa;
      aa('setUserToken', userId);
      localStorage.setItem('_algolia_userToken', userId);
      
      console.log('[Algolia Insights] User authenticated:', userId);
    }
  };
  
  // Clear user token (call this on logout)
  const clearUserToken = () => {
    if (process.client) {
      const newToken = `anonymous-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      if ((window as any)._aa) {
        const aa = (window as any)._aa;
        aa('setUserToken', newToken);
      }
      localStorage.setItem('_algolia_userToken', newToken);
      
      console.log('[Algolia Insights] User logged out, new anonymous token');
    }
  };
  
  return {
    initInsights,
    trackProductClick,
    trackProductView,
    trackAddToCart,
    trackPurchase,
    setAuthenticatedUser,
    clearUserToken,
    getUserToken,
  };
}
